import React, {
  FC,
  ReactElement,
  useContext,
  useEffect,
  useState,
} from "react";
import { defineMessages, useIntl } from "react-intl";
import { useNavigate } from "react-router-dom";
import { Button } from "@natera/form";

import {
  IntlContext,
  PostBillAPIContext,
  PostBillContext,
} from "@app/provider";
import DateOfBirthVerification from "@app/preBill/components/dateOfBirthVerification";
import { routes } from "@app/routing";
import { useQuery, isValidLanguageValue } from "@app/utils";
import { useTokenizedLinks } from "@app/hooks";
import { LinkSourceData } from "@app/hooks/useTokenizedLinks";
import { GuestValidationInput, DOBData } from "@app/postBill/models";
import { PostBillErrors } from "@app/postBill/models/apiControllerModel";
import { Notification } from "@app/components";
import { usePostBillHeapEvent } from "@app/postBill/hooks";
import { HEAP_EVENTS } from "@app/provider/types";
import { updatePostbillLocalStorage } from "@app/postBill/utils/localStorage";
import BillingContactUs from "@app/postBill/components/billingContactUs";
import BillingFooter from "@app/components/footers/billingFooter";

import "./postbillGuestLogin.scss";
import "@app/preBill/preBill.scss";
import { LoginMethod } from "@app/postBill/models/postBillHeap";
import UPSDobVerificationFailed from "@app/ups/pages/private/upsDobVerificationFailed";
import { logMsg } from "@app/utils/logMsg";

const messages = defineMessages({
  postbillGuestLoginPageTitle: {
    id: "postbillGuestLoginPageTitle",
    defaultMessage: "Review and pay your bill",
  },
  postbillGuestLoginPageSubtitle: {
    id: "postbillGuestLoginPageSubtitle",
    defaultMessage:
      "Please enter your date of birth, or log into your Natera Patient Portal account.",
  },
  postbillGuestLoginPageAccountButton: {
    id: "postbillGuestLoginPageAccountButton",
    defaultMessage: "I already have an account",
  },
  postbillGuestLoginDateOfBirthVerificationIssues: {
    id: "postbillGuestLoginDateOfBirthVerificationIssues",
    defaultMessage: "Need help? {contactUs}",
  },
  postbillGuestLoginContactUs: {
    id: "postbillGuestLoginContactUs",
    defaultMessage: "Contact Us",
  },
  postbillGuestLoginCookies: {
    id: "postbillGuestLoginCookies",
    defaultMessage:
      "This site uses cookies to enhance your experience. Any cookies preferences selected on Natera.com will not apply.",
  },
  postbillGuestLoginAcknowledgements: {
    id: "postbillGuestLoginAcknowledgements",
    defaultMessage:
      "By continuing you accept our {termsOfUse}, {privacyPolicy} and {privacyPractices}",
  },
  postbillGuestLoginTermsOfUse: {
    id: "postbillGuestLoginTermsOfUse",
    defaultMessage: "Terms of Use",
  },
  postbillGuestLoginPrivacyPolicy: {
    id: "postbillGuestLoginPrivacyPolicy",
    defaultMessage: "Privacy Policy",
  },
  postbillGuestLoginPrivacyPractices: {
    id: "postbillGuestLoginPrivacyPractices",
    defaultMessage: "Notice of Privacy Practices",
  },
  postbillGuestLoginValidationError: {
    id: "postbillGuestLoginValidationError",
    defaultMessage: "Incorrect date of birth. Please re-enter",
  },
});

const PostbillGuestLogin: FC = () => {
  const intl = useIntl();
  const navigate = useNavigate();
  const { changeLanguage, currentLanguage } = useContext(IntlContext);
  const { setPatientUid, patientUid, setLoginMethod } =
    useContext(PostBillContext);
  const { guestValidation } = useContext(PostBillAPIContext);
  const [loading, setLoading] = useState<boolean>(false);
  const [showError, setShowError] = useState<boolean>(false);
  const { trackPostBillHeapEvent } = usePostBillHeapEvent();
  const ATTEMPTS = 3;
  const [attemptsLeft, setAttemptsLeft] = useState<number>(ATTEMPTS);

  const query = useQuery();
  const token = query.get("token");
  const { getOrderUidPublic, getPatientUidFromTokenHandler } =
    useTokenizedLinks();

  const getPatientLanguage = async (token: string) => {
    const payload = await getOrderUidPublic(token);
    if (payload) {
      setUserLanguage(payload.sourceData);
    }
  };

  const fetchAndSetPatientUid = async (token: string) => {
    const payload = await getPatientUidFromTokenHandler(token);
    if (payload?.patientUid) {
      setPatientUid(payload.patientUid);
    } else {
      logMsg("warn", "postbill.login", "Can't obtain patient id from token!");
    }
  };

  useEffect(() => {
    if (patientUid) {
      trackPostBillHeapEvent({ eventKey: HEAP_EVENTS.upp_view_tokenizedlogin });
    }
  }, [patientUid]);

  const setUserLanguage = async (source: LinkSourceData) => {
    const language = source?.language;
    if (
      language &&
      currentLanguage !== language &&
      isValidLanguageValue(language)
    ) {
      changeLanguage(language);
    }
  };

  useEffect(() => {
    if (!token) {
      navigate(routes.home, { replace: true });
      return;
    }
    getPatientLanguage(token);
    fetchAndSetPatientUid(token);
  }, [token]);

  const title: ReactElement = (
    <section className="billing-guest-login-page__container__title">
      <h2>{intl.formatMessage(messages.postbillGuestLoginPageTitle)}</h2>
      <span>{intl.formatMessage(messages.postbillGuestLoginPageSubtitle)}</span>
    </section>
  );

  const handleGuestValidation = async (
    data: GuestValidationInput
  ): Promise<void> => {
    setShowError(false);
    setLoading(true);

    await guestValidation(data)
      .then(() => {
        setLoginMethod(LoginMethod.TOKENIZED);
        navigate(routes.billingCenter);
        updatePostbillLocalStorage("authType", "guest");
        updatePostbillLocalStorage("guestToken", token);
      })
      .catch((error) => {
        const code = (error?.message || "").toString();
        if (code === PostBillErrors.INCORRECT_DOB) {
          setAttemptsLeft((prev) => {
            const next = prev - 1;
            if (next > 0) {
              setShowError(true);
            }
            return next;
          });
        } else {
          setShowError(true);
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleDateOfBirthSubmit = (date: DOBData) => {
    const guestData: GuestValidationInput = {
      token: token as string,
      dob: { day: date.day, month: date.month, year: date.year },
    };

    handleGuestValidation(guestData);
  };

  const handleAccountButton = () => navigate(`${routes.logIn}?token=${token}`);

  const renderNotification = () => {
    return (
      <Notification
        className="billing-guest-login-page__container__notification"
        type="error"
      >
        <p className="billing-guest-login-page__container__notification__content">
          {intl.formatMessage(messages.postbillGuestLoginValidationError)}
        </p>
      </Notification>
    );
  };

  if (attemptsLeft <= 0) {
    return (
      <UPSDobVerificationFailed
        onCreateAccount={() => navigate(routes.signUp)}
      />
    );
  }

  return (
    <div className="billing-guest-login-page billing-gradient">
      <div className="billing-guest-login-page__container">
        {title}
        {showError && renderNotification()}
        <DateOfBirthVerification
          loading={loading}
          onSubmit={handleDateOfBirthSubmit}
        />
        <Button
          outlined
          className="billing-guest-login-page__account-button"
          onClick={handleAccountButton}
        >
          {intl.formatMessage(messages.postbillGuestLoginPageAccountButton)}
        </Button>
        <BillingContactUs />
        <BillingFooter />
      </div>
    </div>
  );
};

export default PostbillGuestLogin;
